(function () {
  'use strict';

  const STORAGE_KEY = 'theme_wishlist_v1';

  function normalizeHandle(value) {
    if (!value) return '';

    let handle = String(value).trim();

    try {
      if (handle.startsWith('http://') || handle.startsWith('https://')) {
        handle = new URL(handle).pathname;
      }
    } catch (error) {
      console.warn('Invalid wishlist value:', value);
    }

    handle = handle
      .split('?')[0]
      .split('#')[0]
      .replace(/^\/+|\/+$/g, '');

    if (handle.includes('/products/')) {
      handle = handle.split('/products/')[1].split('/')[0];
    }

    return handle.toLowerCase();
  }

  function parseStoredValue(rawValue) {
    if (!rawValue) return [];

    try {
      const parsed = JSON.parse(rawValue);

      if (Array.isArray(parsed)) {
        return parsed.map(function (item) {
          if (typeof item === 'string' || typeof item === 'number') {
            return item;
          }

          if (item && typeof item === 'object') {
            return (
              item.handle ||
              item.product_handle ||
              item.productHandle ||
              item.url ||
              item.product_url ||
              ''
            );
          }

          return '';
        });
      }

      if (parsed && typeof parsed === 'object') {
        if (Array.isArray(parsed.items)) {
          return parsed.items;
        }

        return Object.keys(parsed).filter(function (key) {
          return Boolean(parsed[key]);
        });
      }
    } catch (error) {
      return String(rawValue).split(',');
    }

    return [];
  }

  function migrateOldWishlist() {
    const oldKeys = [
      'wishlist',
      'wishlistItems',
      'theme-wishlist',
      'wishlist_products',
      'wishlistProducts'
    ];

    for (const key of oldKeys) {
      const oldValue = localStorage.getItem(key);

      if (!oldValue) continue;

      const handles = parseStoredValue(oldValue)
        .map(normalizeHandle)
        .filter(Boolean);

      if (handles.length) {
        const uniqueHandles = Array.from(new Set(handles));

        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify(uniqueHandles)
        );

        return uniqueHandles;
      }
    }

    return [];
  }

  function getWishlist() {
    const storedValue = localStorage.getItem(STORAGE_KEY);

    let handles = parseStoredValue(storedValue)
      .map(normalizeHandle)
      .filter(Boolean);

    handles = Array.from(new Set(handles));

    if (!handles.length) {
      handles = migrateOldWishlist();
    }

    return handles;
  }

  function saveWishlist(handles) {
    const cleanHandles = Array.from(
      new Set(
        handles
          .map(normalizeHandle)
          .filter(Boolean)
      )
    );

    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(cleanHandles)
    );

    window.dispatchEvent(
      new CustomEvent('wishlist:updated', {
        detail: {
          handles: cleanHandles
        }
      })
    );

    return cleanHandles;
  }

  function isWishlisted(handle) {
    const normalizedHandle = normalizeHandle(handle);

    return getWishlist().includes(normalizedHandle);
  }

  function addToWishlist(handle) {
    const normalizedHandle = normalizeHandle(handle);

    if (!normalizedHandle) return;

    const wishlist = getWishlist();

    if (!wishlist.includes(normalizedHandle)) {
      wishlist.push(normalizedHandle);
    }

    saveWishlist(wishlist);
  }

  function removeFromWishlist(handle) {
    const normalizedHandle = normalizeHandle(handle);

    const wishlist = getWishlist().filter(function (item) {
      return item !== normalizedHandle;
    });

    saveWishlist(wishlist);
  }

  function toggleWishlist(handle) {
    if (isWishlisted(handle)) {
      removeFromWishlist(handle);
      return false;
    }

    addToWishlist(handle);
    return true;
  }

  function updateWishlistCount() {
    const count = getWishlist().length;

    document
      .querySelectorAll('[data-wishlist-count]')
      .forEach(function (countElement) {
        countElement.textContent = count;

        const countWrapper = countElement.closest('.have_info');

        if (countWrapper) {
          countWrapper.hidden = count === 0;
        }
      });
  }

  function updateWishlistButtons() {
    document
      .querySelectorAll('[data-wishlist-product]')
      .forEach(function (button) {
        const handle = button.dataset.wishlistProduct;
        const active = isWishlisted(handle);

        button.classList.toggle('is-wishlisted', active);
        button.setAttribute('aria-pressed', String(active));

        const icon = button.querySelector('i');

        if (icon) {
          icon.classList.toggle('bi-heart', !active);
          icon.classList.toggle('bi-heart-fill', active);
        }

        const label = button.querySelector('[data-wishlist-label]');

        if (label) {
          label.textContent = active
            ? 'Remove from wishlist'
            : 'Add to wishlist';
        }
      });
  }

  function escapeHtml(value) {
    const element = document.createElement('div');
    element.textContent = value || '';
    return element.innerHTML;
  }

  function formatMoney(cents) {
    if (
      window.Shopify &&
      typeof window.Shopify.formatMoney === 'function'
    ) {
      return window.Shopify.formatMoney(cents);
    }

    return (Number(cents || 0) / 100).toFixed(2);
  }

  async function fetchProduct(handle) {
    const root =
      window.Shopify &&
      window.Shopify.routes &&
      window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';

    const response = await fetch(
      root + 'products/' + encodeURIComponent(handle) + '.js',
      {
        headers: {
          Accept: 'application/json'
        }
      }
    );

    if (!response.ok) {
      throw new Error(
        'Unable to load product: ' + handle
      );
    }

    return response.json();
  }

  function createProductCard(product) {
    const productUrl =
      product.url || '/products/' + product.handle;

    const image =
      product.featured_image ||
      (product.images && product.images[0]) ||
      '';

    const availableVariant =
      product.variants &&
      product.variants.find(function (variant) {
        return variant.available;
      });

    const firstVariant =
      availableVariant ||
      (product.variants && product.variants[0]);

    return `
      <article
        class="wishlist-card card h-100"
        data-wishlist-item="${escapeHtml(product.handle)}"
      >
        <div class="position-relative">
          <a href="${escapeHtml(productUrl)}">
            ${
              image
                ? `
                  <img
                    src="${escapeHtml(image)}"
                    alt="${escapeHtml(product.title)}"
                    class="card-img-top"
                    loading="lazy"
                    width="720"
                    height="900"
                  >
                `
                : `
                  <div class="wishlist-card__placeholder"></div>
                `
            }
          </a>

          <button
            type="button"
            class="wishlist-card__remove btn btn-reset"
            data-wishlist-remove="${escapeHtml(product.handle)}"
            aria-label="Remove ${escapeHtml(product.title)} from wishlist"
          >
            <i class="bi bi-x-lg" aria-hidden="true"></i>
          </button>
        </div>

        <div class="card-body d-flex flex-column">
          <h3 class="h5 card-title">
            <a href="${escapeHtml(productUrl)}">
              ${escapeHtml(product.title)}
            </a>
          </h3>

          <div class="mb-3">
            ${formatMoney(product.price)}
          </div>

          ${
            firstVariant
              ? `
                <form
                  method="post"
                  action="/cart/add"
                  class="mt-auto"
                >
                  <input
                    type="hidden"
                    name="id"
                    value="${escapeHtml(firstVariant.id)}"
                  >

                  <input
                    type="hidden"
                    name="quantity"
                    value="1"
                  >

                  <button
                    type="submit"
                    class="btn btn-primary w-100"
                    ${firstVariant.available ? '' : 'disabled'}
                  >
                    ${
                      firstVariant.available
                        ? 'Add to cart'
                        : 'Sold out'
                    }
                  </button>
                </form>
              `
              : ''
          }
        </div>
      </article>
    `;
  }

  async function renderWishlistPage() {
    const page = document.querySelector(
      '[data-wishlist-page]'
    );

    if (!page) return;

    const grid = page.querySelector(
      '[data-wishlist-grid]'
    );

    const emptyMessage = page.querySelector(
      '[data-wishlist-empty]'
    );

    const loadingMessage = page.querySelector(
      '[data-wishlist-loading]'
    );

    const errorMessage = page.querySelector(
      '[data-wishlist-error]'
    );

    const handles = getWishlist();

    grid.innerHTML = '';
    emptyMessage.hidden = true;
    errorMessage.hidden = true;

    if (!handles.length) {
      loadingMessage.hidden = true;
      emptyMessage.hidden = false;
      return;
    }

    loadingMessage.hidden = false;

    const results = await Promise.allSettled(
      handles.map(fetchProduct)
    );

    const products = [];
    const unavailableHandles = [];

    results.forEach(function (result, index) {
      if (result.status === 'fulfilled') {
        products.push(result.value);
      } else {
        unavailableHandles.push(handles[index]);
        console.error(result.reason);
      }
    });

    if (unavailableHandles.length) {
      const validHandles = handles.filter(function (handle) {
        return !unavailableHandles.includes(handle);
      });

      saveWishlist(validHandles);
    }

    loadingMessage.hidden = true;

    if (!products.length) {
      emptyMessage.hidden = false;

      if (unavailableHandles.length) {
        errorMessage.textContent =
          'Saved products could not be loaded.';
        errorMessage.hidden = false;
      }

      return;
    }

    grid.innerHTML = products
      .map(createProductCard)
      .join('');

    if (unavailableHandles.length) {
      errorMessage.textContent =
        'Some unavailable products were removed from your wishlist.';
      errorMessage.hidden = false;
    }
  }

  // document.addEventListener('click', function (event) {
  //   const wishlistButton = event.target.closest(
  //     '[data-wishlist-product]'
  //   );

  //   if (wishlistButton) {
  //     event.preventDefault();

  //     const handle =
  //       wishlistButton.dataset.wishlistProduct;

  //     toggleWishlist(handle);
  //     updateWishlistCount();
  //     updateWishlistButtons();

  //     return;
  //   }

  //   const removeButton = event.target.closest(
  //     '[data-wishlist-remove]'
  //   );

  //   if (removeButton) {
  //     event.preventDefault();

  //     const handle =
  //       removeButton.dataset.wishlistRemove;

  //     removeFromWishlist(handle);

  //     const productCard = removeButton.closest(
  //       '[data-wishlist-item]'
  //     );

  //     if (productCard) {
  //       productCard.remove();
  //     }

  //     updateWishlistCount();
  //     updateWishlistButtons();

  //     const page = document.querySelector(
  //       '[data-wishlist-page]'
  //     );

  //     if (
  //       page &&
  //       !page.querySelector('[data-wishlist-item]')
  //     ) {
  //       const emptyMessage = page.querySelector(
  //         '[data-wishlist-empty]'
  //       );

  //       if (emptyMessage) {
  //         emptyMessage.hidden = false;
  //       }
  //     }
  //   }
  // });
document.addEventListener(
  'click',
  function (event) {
    const wishlistButton = event.target.closest(
      '[data-wishlist-product]'
    );

    if (wishlistButton) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const handle =
        wishlistButton.getAttribute('data-wishlist-product');

      if (!handle) {
        console.error('Wishlist product handle is missing.');
        return;
      }

      toggleWishlist(handle);
      updateWishlistCount();
      updateWishlistButtons();

      return false;
    }

    const removeButton = event.target.closest(
      '[data-wishlist-remove]'
    );

    if (removeButton) {
      event.preventDefault();
      event.stopPropagation();

      const handle =
        removeButton.getAttribute('data-wishlist-remove');

      removeFromWishlist(handle);

      const productCard = removeButton.closest(
        '[data-wishlist-item]'
      );

      if (productCard) {
        productCard.remove();
      }

      updateWishlistCount();
      updateWishlistButtons();

      const wishlistPage = document.querySelector(
        '[data-wishlist-page]'
      );

      if (
        wishlistPage &&
        !wishlistPage.querySelector('[data-wishlist-item]')
      ) {
        const emptyMessage = wishlistPage.querySelector(
          '[data-wishlist-empty]'
        );

        if (emptyMessage) {
          emptyMessage.hidden = false;
        }
      }
    }
  },
  true
);

  document.addEventListener(
    'DOMContentLoaded',
    function () {
      updateWishlistCount();
      updateWishlistButtons();
      renderWishlistPage();
    }
  );

  window.addEventListener(
    'wishlist:updated',
    function () {
      updateWishlistCount();
      updateWishlistButtons();
    }
  );

  window.addEventListener(
    'storage',
    function (event) {
      if (event.key === STORAGE_KEY) {
        updateWishlistCount();
        updateWishlistButtons();
        renderWishlistPage();
      }
    }
  );

  window.ThemeWishlist = {
    getAll: getWishlist,
    add: addToWishlist,
    remove: removeFromWishlist,
    toggle: toggleWishlist,
    contains: isWishlisted
  };
})();